import { useState } from 'react';
import dashboardCircle from '@assets/icons/dashboard-circle.png';
import arrowCircleDown from '@assets/icons/arrowCircleDown.svg';
import arrowCircleUP from '@assets/icons/arrowCircleUp.svg';
import flowline from '@assets/icons/flowline.svg';
import { Spin, Empty, Tooltip } from 'antd';
import ScrollContainer from 'react-indiana-drag-scroll';

const COLUMNS = [
  { isBtn: true, label: 'Group' },
  {
    label: 'Projects',
    tooltip: 'No. of projects with complete wastage information',
    colored: false,
  },
  {
    label: 'Quantity Processed - Quarterly',
    subLabel: '(in MT)',
    tooltip: 'Total reinforcement quantity processed in the quarter',
    colored: true,
  },
  {
    label: 'Quantity Processed',
    subLabel: 'with BBS optimization tool (in MT)',
    tooltip:
      'Reinforcement quantity processed in the quarter with the use of optimization tool (in MT)',
    colored: true,
  },
  {
    label: 'Quantity Processed',
    subLabel: 'without BBS optimization tool (in MT)',
    tooltip:
      'Reinforcement quantity processed in the quarter without the use of optimization tool (in MT)',
    colored: true,
  },
  {
    label: 'Total wastage',
    subLabel: 'Effective wastage of reinforcement in the quarter (%)',
    tooltip:
      'Effective wastage in the quarter which was processed with or without BBS optimization tool (%)',
    colored: false,
  },
  {
    label: 'A1. Wastage',
    subLabel: 'Optimized using BBS tool (%)',
    tooltip: 'Optimized wastage as per BBS optimization tool',
    colored: true,
  },
  {
    label: 'A2. Wastage',
    subLabel: 'Realized during execution of A1. Wastage (%)',
    tooltip: 'Actual wastage incured post output from BBS optimization tool',
    colored: true,
  },
  {
    label: 'B. Wastage',
    subLabel: 'As executed without BBS optimization tool',
    tooltip:
      'Wastage on the quantity not executed through BBS optimization tool',
    colored: true,
  },
  {
    label: 'BBS Tool Adoption',
    subLabel: '% quantity executed through BBS optimization tool',
    tooltip:
      'Percentage of reinforcement work carried out using BBS optimization tool',
    colored: false,
  },
  // {
  //   label: 'Actual Wastage',
  //   subLabel: 'Derived wastage from BBS optimization',
  //   tooltip: 'BBS based wastage estimate (Tonnage × Actual wastage)',
  //   colored: true,
  // },
  // {
  //   label: 'Optimal Wastage',
  //   subLabel: 'System estimated wastage',
  //   tooltip: 'Wastage percentage generated by the BBS system = Tonnage(with BBS)*Wastage BBS system generated',
  //   colored: true,
  // },
  {
    label: 'Wastage (in MT)',
    subLabel: 'Overall wastage incured',
    tooltip:
      'Overall wastage incured in quarter with or without BBS opotimization tool',
    colored: true,
  },
  {
    label: 'Budgeted Wastage',
    subLabel: '(in %)',
    tooltip: 'From CB/CTC',
    colored: false,
  },
  {
    label: 'Savings wrt budgeted wastage',
    subLabel: '(in %)',
    tooltip: 'Difference of Budgeted wastage & Actual wastage',
    colored: false,
  },
  {
    label: 'Savings wrt budgeted wastage',
    subLabel: '(in MT)',
    tooltip: 'Quantity savings in MT',
    colored: true,
  },
  {
    label: 'Savings value',
    subLabel: '(in Rs Lakhs)',
    tooltip: '@ steel rates of INR 55,000 per MT',
    colored: true,
  },
];

export default function WastageProjectInfo({ dataSource, loading }) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const getTransformProjectsData = (
    data = [],
    prefix = '',
    level = 1,
    parentId = '',
  ) => {
    return data.map((item, index) => {
      const id = parentId ? `${parentId}-${index + 1}` : `${index + 1}`;
      // const name = `${prefix}${data.length > 1 ? `-${index + 1}` : ''}`;
      const name = item.name;

      const mappedItem = {
        id,
        name,
        project_count: item.project_count || null,
        tonnage_quarterly: item.tonnage_quarterly,
        tonnage_with_bbs: item.tonnage_with_bbs,
        tonnage_without_bbs: item.tonnage_without_bbs,
        total_wastage: item.total_wastage,
        wastage_with_system: item.wastage_with_system,
        wastage_with_actual: item.wastage_with_actual,
        wastage_without_bbs: item.wastage_without_bbs,
        adoption: item.adoption,
        wastage_in_mt: item.wastage_in_mt,
        actual_wastage: item.actual_wastage,
        optimal_wastage: item.optimal_wastage,
        budgeted_wastage: item.budgeted_wastage,
        savings_per: item.savings_per,
        savings_in_ton: item.savings_in_ton,
        savings_in_lakh: item.savings_in_lakh,
        isChild: prefix === 'SBU' || prefix === 'BU',
      };

      if (item.SBU) {
        mappedItem.children = getTransformProjectsData(
          item.SBU,
          'SBU',
          level + 1,
          id,
        );
      } else if (item.BU) {
        mappedItem.children = getTransformProjectsData(
          item.BU,
          'BU',
          level + 1,
          id,
        );
      } else if (item.projects) {
        mappedItem.children = item.projects.map((project, projIndex) => ({
          id: `${id}-${projIndex + 1}`,
          name: project.code,
          project_count: null,
          tonnage_quarterly: project.tonnage_quarterly,
          tonnage_with_bbs: project.tonnage_with_bbs,
          tonnage_without_bbs: project.tonnage_without_bbs,
          total_wastage: project.total_wastage,
          wastage_in_mt: project.wastage_in_mt,
          wastage_with_system: project.wastage_with_system,
          wastage_with_actual: project.wastage_with_actual,
          wastage_without_bbs: project.wastage_without_bbs,
          adoption: project.adoption,
          actual_wastage: project.actual_wastage,
          optimal_wastage: project.optimal_wastage,
          budgeted_wastage: project.budgeted_wastage,
          savings_per: project.savings_per,
          savings_in_ton: project.savings_in_ton,
          savings_in_lakh: project.savings_in_lakh,
          isChild: true,
        }));
      }

      return mappedItem;
    });
  };

  const mappedData = getTransformProjectsData(dataSource, 'SBG');

  const toggleRow = (id, e) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const TableRow = ({ row, level = 0 }) => {
    const isExpanded = expandedRows.has(row.id);
    const hasChildren = row.children && row.children.length > 0;

    return (
      <>
        <tr className={`relative bg-white`}>
          <td
            className={`px-3 py-4 border-b sticky left-0 ${row.isChild ? 'bg-[#7795C4] border-[#7e9bca]' : 'bg-[#1C4E9D] border-[#7e9bca] '}`}
          >
            {/* flow line */}
            {level > 0 && (
              <div
                className="absolute top-0 left-0 h-full"
                style={{
                  paddingLeft:
                    level === 1
                      ? '1rem'
                      : level === 2
                        ? '3.5rem'
                        : level === 3
                          ? '6rem'
                          : 0,
                }}
              >
                {/* <img alt="flowline" className="w-7 h-8" src={flowline} /> */}
              </div>
            )}

            {/* label */}
            <div className="flex items-center select-none">
              <div
                style={{ paddingLeft: `${level * 40}px` }}
                className="flex items-center text-nowrap"
              >
                {hasChildren && (
                  <div
                    className="cursor-pointer"
                    onClick={(e) => toggleRow(row.id, e)}
                  >
                    <img
                      src={isExpanded ? arrowCircleUP : arrowCircleDown}
                      alt="arrow"
                      className="w-[18px] h-[18px]"
                    />
                  </div>
                )}
                {/* {!hasChildren && <div className="mr-2 w-6" />} */}
                <span className=" select-none text-xs font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-white ml-1">
                  {row.name}
                </span>
              </div>
            </div>
          </td>

          <td className="py-4 px-3 border-b select-none text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">{row.project_count}</span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.tonnage_quarterly) === 0
                ? '-'
                : Math.round(Number(row.tonnage_quarterly))}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.tonnage_with_bbs) === 0
                ? '-'
                : Number(row.tonnage_with_bbs).toFixed(2)}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.tonnage_without_bbs) === 0
                ? '-'
                : Number(row.tonnage_without_bbs).toFixed(2)}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.total_wastage) === 0
                ? '-'
                : `${Number(row.total_wastage).toFixed(2)}%`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.wastage_with_system) === 0
                ? '-'
                : `${Number(row.wastage_with_system).toFixed(2)}%`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.wastage_with_actual) === 0
                ? '-'
                : `${Number(row.wastage_with_actual).toFixed(2)}%`}
            </span>
          </td>

          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.wastage_without_bbs) === 0
                ? '-'
                : `${Number(row.wastage_without_bbs).toFixed(2)}%`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.adoption) === 0
                ? '-'
                : `${Number(row.adoption).toFixed(2)}%`}
            </span>
          </td>
          {/* <td className="py-4 px-3 border-b select-none bg-[#E8EDF5] text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.actual_wastage).toFixed(2)}
            </span>
          </td> */}
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5]  text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.wastage_in_mt) === 0
                ? '-'
                : `${Number(row.wastage_in_mt).toFixed(2)}`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.budgeted_wastage) === 0
                ? '-'
                : `${Number(row.budgeted_wastage).toFixed(2)}%`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.savings_per) === 0
                ? '-'
                : `${Number(row.savings_per).toFixed(2)}%`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5]  text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.savings_in_ton) === 0
                ? '-'
                : `${Number(row.savings_in_ton).toFixed(2)}`}
            </span>
          </td>
          <td className="py-4 px-3 border-b select-none bg-[#E8EDF5]  text-sm font-medium font-['SF-Pro-Text-Regular, sans-serif'] text-tertiary-placeholder">
            <span className="flex justify-center">
              {Number(row.savings_in_lakh) === 0
                ? '-'
                : `${Number(row.savings_in_lakh).toFixed(1)}`}
            </span>
          </td>
        </tr>
        {isExpanded &&
          hasChildren &&
          row.children.map((child) => (
            <TableRow key={child.id} row={child} level={level + 1} />
          ))}
      </>
    );
  };

  const renderHeader = () => {
    return COLUMNS.map((column, index) => (
      <th
        key={index}
        className={`py-2 ${column.isBtn ? 'border-b border-[#7e9bca]' : 'border-b'} place-items-center px-4 ${index === 0 ? 'w-60' : 'w-60 whitespace-nowrap'} text-sm font-['SF-Pro-Text-Regular, sans-serif'] text-primary-bg-tint text-left font-medium ${index === 0 ? 'bg-[#1C4E9D] sticky left-0' : column.colored ? 'bg-[#E8EDF5]' : ''}`}
      >
        {column.isBtn ? (
          <div className="flex !text-white items-center justify-center gap-2">
            <span className="leading-none text-xs !p-0">{column.label}</span>
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center">
            <div className="flex gap-2 justify-center items-center">
              <span className="leading-none text-xs !p-0">{column.label}</span>
              <Tooltip
                title={column.tooltip}
                overlayInnerStyle={{ fontSize: '10px' }}
              >
                <img
                  className="w-3.5 h-3.5 cursor-pointer"
                  src={dashboardCircle}
                  alt="Info Icon"
                />
              </Tooltip>
            </div>
            {column.subLabel && (
              <span className="font-normal text-[10px]">{column.subLabel}</span>
            )}
          </div>
        )}
      </th>
    ));
  };

  return (
    <div className="w-full overflow-hidden rounded-[10px] mt-2">
      <ScrollContainer
        horizontal={mappedData.length}
        // className={`${!mappedData.length ? 'overflow-hidden' : 'overflow-auto hide-scroll'}`}
      >
        <table className="w-full min-w-max bg-white border-collapse table-fixed">
          <thead>
            <tr className="">{renderHeader()}</tr>
          </thead>
          {mappedData.length ? (
            <tbody>
              {mappedData.map((row, i) => (
                <TableRow key={row.id} row={row} />
              ))}
            </tbody>
          ) : null}
        </table>

        {loading ? (
          <div className="flex items-center bg-[#F6F7F9] justify-center w-full">
            <Spin className="py-12" />
          </div>
        ) : !mappedData.length ? (
          <div className="flex items-center bg-[#F6F7F9] justify-center w-full">
            <Empty className="py-6" />
          </div>
        ) : null}
      </ScrollContainer>
    </div>
  );
}
